import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Alert, Button, Drawer, Modal, Radio, RadioGroup, Select, TextInput, Textarea, Toggle, Plus, Trash01 } from '@opendate-app/setlist';

import FieldTypeTiles from './FieldTypeTiles';
import { ALLOWED_CHARACTER_OPTIONS, acceptsInputRules, canSaveCustomField, flagValue, flagsFor, isBuiltIn, typeLabel } from './customFieldHelpers';
import { errorText } from '../../../shared/toastHelper';
import '../../ConfirmTicketing/components/TicketDrawer.css';

const newOption = () => ({ name: '', key: `new-${Date.now()}-${Math.random()}` });

const newField = () => ({
  name: '',
  type: 'CustomFields::Text',
  help_text: '',
  enabled: true,
  fan_visible: true,
  required: false,
  custom_field_options: [newOption(), newOption()]
});

const ASK_PER_CHOICES = [
  { value: 'order', title: 'Once per order', description: 'The buyer answers once, no matter how many tickets they buy.' },
  { value: 'ticket', title: 'Once per ticket', description: 'Asked for every ticket in the order — use for attendee details.' }
];

const CustomFieldDrawer = ({ customField, onSave, onDelete, onClose, context, venueSettingsUrl, venueName, eventName, ticketTypes, askPerAvailable }) => {
  const [draft, setDraft] = useState(customField
    ? {
      ...customField,
      custom_field_options: customField.custom_field_options || [],
      ticket_type_ids: (customField.ticket_type_ids || []).filter((id) => ticketTypes.some((tt) => Number(tt.id) === Number(id)))
    }
    : newField());
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const isNew = !draft.id;
  const isEvent = context === 'event';
  const fromVenue = isEvent && !isNew && !draft.event_only;
  const venueLabel = venueName || 'the venue';
  const title = isNew ? (isEvent ? 'Add event field' : 'Add field') : (fromVenue ? 'Edit for this event' : 'Edit field');
  const subtitle = isEvent
    ? [eventName, fromVenue ? 'override for this event' : 'this event only'].filter(Boolean).join(' · ')
    : [venueName, "applies to this venue's events"].filter(Boolean).join(' · ');
  const merge = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const ticketTypeOptions = ticketTypes.map((ticketType) => ({ value: ticketType.id, label: ticketType.name }));
  const selectedTicketTypeIds = ticketTypes
    .filter((ticketType) => (draft.ticket_type_ids || []).some((id) => Number(id) === Number(ticketType.id)))
    .map((ticketType) => ticketType.id);
  const setOptions = (fn) => setDraft((d) => ({ ...d, custom_field_options: fn(d.custom_field_options) }));

  const run = async (action) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };

  return (
    <>
      <Drawer
        open
        backdrop
        width={560}
        data-lift-conductor-orb
        title={title}
        subtitle={subtitle}
        onClose={onClose}
        footer={
          <div style={{ display: 'flex', width: '100%', gap: 8 }}>
            {!isNew && !isBuiltIn(draft) && !fromVenue && (
              <Button variant="destructive" icon={Trash01} disabled={busy} onClick={() => setConfirmingDelete(true)}>
                Delete field
              </Button>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button variant="primary" disabled={busy || !canSaveCustomField(draft)} onClick={() => run(() => onSave(draft))}>
                {isNew ? 'Add field' : 'Save'}
              </Button>
            </div>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {error && <Alert color="error" role="alert" title="Could not save field" description={error} />}

          {fromVenue && (
            <Alert
              color="gray"
              description={(
                <>
                  {`Label, type and options come from ${venueLabel}'s ticketing settings.`}
                  {venueSettingsUrl && <>{' '}<a href={venueSettingsUrl} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>Edit venue default</a></>}
                </>
              )}
            />
          )}

          <TextInput
            id="custom-field-name"
            label="Label *"
            disabled={fromVenue}
            value={draft.name || ''}
            onChange={(e) => merge({ name: e.target.value })}
          />

          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--sl-text-secondary)', marginBottom: 6 }}>Field type</div>
            {isBuiltIn(draft) ? (
              <div style={{ fontSize: 14, color: 'var(--sl-text-tertiary)' }}>{typeLabel(draft.type)}</div>
            ) : (
              <FieldTypeTiles value={draft.type} onChange={(type) => merge({ type })} disabled={!isNew} />
            )}
          </div>

          {!isBuiltIn(draft) && (
            <Textarea
              label="Help text"
              rows={2}
              placeholder="Shown under the field at checkout"
              disabled={fromVenue}
              value={draft.help_text || ''}
              onChange={(e) => merge({ help_text: e.target.value })}
            />
          )}

          {acceptsInputRules(draft.type) && (
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <TextInput
                  id="custom-field-max-length"
                  label="Max characters"
                  type="number"
                  min={1}
                  max={5000}
                  placeholder="No limit"
                  disabled={fromVenue}
                  value={draft.max_length ?? ''}
                  onChange={(e) => merge({ max_length: e.target.value })}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Select
                  label="Allowed characters"
                  options={ALLOWED_CHARACTER_OPTIONS}
                  disabled={fromVenue}
                  value={draft.allowed_characters || ''}
                  onChange={(value) => merge({ allowed_characters: value || '' })}
                />
              </div>
            </div>
          )}

          {draft.type === 'CustomFields::Select' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--sl-text-secondary)' }}>Options *</div>
              {draft.custom_field_options.map((option, index) => (option._destroy ? null : (
                <div key={option.id || option.key} style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <TextInput
                      aria-label={`Option ${index + 1}`}
                      disabled={fromVenue}
                      value={option.name || ''}
                      onChange={(e) => setOptions((opts) => opts.map((o, i) => (i === index ? { ...o, name: e.target.value } : o)))}
                    />
                  </div>
                  {!fromVenue && (
                    <Button
                      variant="tertiary"
                      icon={Trash01}
                      aria-label="Remove option"
                      onClick={() => setOptions((opts) => (option.id
                        ? opts.map((o, i) => (i === index ? { ...o, _destroy: true } : o))
                        : opts.filter((_, i) => i !== index)))}
                    />
                  )}
                </div>
              )))}
              {!fromVenue && (
                <div>
                  <Button variant="tertiary" size="sm" icon={Plus} onClick={() => setOptions((opts) => [...opts, newOption()])}>
                    Add option
                  </Button>
                </div>
              )}
            </div>
          )}

          {draft.type === 'CustomFields::File' && (
            <div style={{ fontSize: 14, color: 'var(--sl-text-tertiary)' }}>
              Fans can upload one PDF, JPG or PNG up to 10 MB. Files are attached to the order.
            </div>
          )}

          {askPerAvailable && (
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--sl-text-secondary)', marginBottom: 6 }}>Ask this</div>
              <RadioGroup name="custom-field-ask-per" value={draft.ask_per || 'order'} onChange={(value) => merge({ ask_per: value })}>
                {ASK_PER_CHOICES.map((choice) => (
                  <Radio
                    key={choice.value}
                    id={`custom-field-ask-per-${choice.value}`}
                    value={choice.value}
                    disabled={isBuiltIn(draft)}
                    label={(
                      <span>
                        <span style={{ display: 'block', fontWeight: 500 }}>{choice.title}</span>
                        <span style={{ display: 'block', fontSize: 13, color: 'var(--sl-text-tertiary)' }}>{choice.description}</span>
                      </span>
                    )}
                  />
                ))}
              </RadioGroup>
            </div>
          )}

          {isEvent && (
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--sl-text-secondary)', marginBottom: 6 }}>Show for</div>
              <RadioGroup
                name="custom-field-show-for"
                value={draft.ticket_type_targeted ? 'specific' : 'all'}
                onChange={(value) => merge({ ticket_type_targeted: value === 'specific', ticket_type_ids: [] })}
              >
                <Radio id="custom-field-show-for-all" value="all" label="All ticket types" />
                <Radio id="custom-field-show-for-specific" value="specific" label="Specific ticket types" />
              </RadioGroup>
              {draft.ticket_type_targeted && (
                <div style={{ marginTop: 8 }}>
                  <Select
                    id="custom-field-ticket-type-ids"
                    label="Ticket types *"
                    placeholder="Select"
                    multiple
                    searchable
                    clearable
                    options={ticketTypeOptions}
                    value={selectedTicketTypeIds}
                    onChange={(ids) => merge({ ticket_type_ids: ids || [] })}
                  />
                </div>
              )}
            </div>
          )}

          {flagsFor(context, { withFanVisible: !fromVenue }).map((t) => (
            <div key={t.key}>
              <Toggle
                id={`custom-field-${t.key}`}
                label={t.label}
                checked={flagValue(draft, t.key)}
                onChange={(e) => merge({ [t.key]: e?.target ? e.target.checked : e })}
              />
              <div style={{ fontSize: 13, color: 'var(--sl-text-tertiary)', marginTop: 2 }}>{t.description}</div>
            </div>
          ))}
        </div>
      </Drawer>
      <Modal
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        type="error"
        title="Delete this field?"
        confirmLabel="Delete field"
        onConfirm={() => {
          setConfirmingDelete(false);
          run(onDelete);
        }}
      >
        <p className="mb-0">
          {isEvent
            ? `"${draft.name}" will be removed from this event's checkout.`
            : `"${draft.name}" will be removed from checkout for every event at this venue that hasn't customized its order form. This can't be undone.`}
        </p>
      </Modal>
    </>
  );
};

CustomFieldDrawer.propTypes = {
  customField: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  context: PropTypes.oneOf(['venue', 'event']),
  venueSettingsUrl: PropTypes.string,
  venueName: PropTypes.string,
  eventName: PropTypes.string,
  ticketTypes: PropTypes.arrayOf(PropTypes.object),
  askPerAvailable: PropTypes.bool
};

CustomFieldDrawer.defaultProps = {
  customField: null,
  context: 'venue',
  venueSettingsUrl: null,
  venueName: null,
  eventName: null,
  ticketTypes: []
};

export default CustomFieldDrawer;
